import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cssVars from '@figma/fpl-tokens/css-vars';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { Box } from '@/UI/Box/Box';
import { AppTable } from '@/UI/AppTable/AppTable';
import { TextSmRg } from '@/UI/AppTypography/AppTypography';
import { getTimeDiffInWords } from '@/utils/date.utils';
import { createGetLatestUpdateInFolder } from '@/utils/folder.utils';
import { color } from '@/colors';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { FlexCenVer } from '@/UI/styles';
import { useDragAndDrop } from '@/hooks/useDragAndDrop';
import { useUserStore } from '@/state/user.state';
import { I18N_KEYS } from '@/language/keys';
import { useGoToWorkflow } from '@/hooks/useGoToWorkflow';
import { EnhancedTableHead } from './components/EnhancedTableHead';
import { FolderThumbnail } from './components/FolderThumbnail';
import { DashboardInlineEditInput } from './DashboardInlineEditInput';
import {
  descendingComparator,
  calculateFolderRecipeData,
  getListItemDateSortValue,
  type ListItem,
} from './utils/listView.utils';
import { dashboardDisplayName, dashboardRowId } from './utils/dashboard.utils';
import { defaultWorkflowCover } from './utils/defaultCover';
import type { DashboardRecipe } from '@/types/recipes';
import type { FolderResponseDto } from '@/types/folder.types';
import type { Order, AllowedRecipeKeys } from './types/listView.types';
import type { DashboardItem, EditingNameState } from './DashboardMain';

interface ListRowNameProps {
  isEditing: boolean;
  name: string;
  displayValue: string;
  onConfirm: (name: string) => void;
  onCancel: () => void;
}

/** A row's name and its rename draft. The draft stays here so typing re-renders this row, not the whole list. */
function ListRowName({ isEditing, name, displayValue, onConfirm, onCancel }: ListRowNameProps) {
  const [editingValue, setEditingValue] = useState(name);

  useEffect(() => {
    if (isEditing) {
      setEditingValue(name);
    }
  }, [isEditing, name]);

  return (
    <DashboardInlineEditInput
      isEditing={isEditing}
      value={editingValue}
      onChange={setEditingValue}
      onConfirm={() => onConfirm(editingValue)}
      onCancel={() => {
        setEditingValue(name);
        onCancel();
      }}
      displayValue={displayValue}
    />
  );
}

interface DashboardListViewProps {
  recipes: (DashboardRecipe & { folderId?: string | null; ownerName?: string; owner?: string })[];
  /** The section's rows, minus the active kind filter: feeds a folder's count, posters and last activity. */
  allRecipes?: (DashboardRecipe & { folderId?: string | null; ownerName?: string; owner?: string })[];
  folders?: FolderResponseDto[];
  allFolders?: FolderResponseDto[];
  recipeEditState: EditingNameState;
  handleRenameRecipe: (id: string, name: string) => void;
  handleContextMenu: (
    event: React.MouseEvent<HTMLDivElement>,
    id: string,
    itemType?: DashboardItem,
    rowId?: string,
  ) => void;
  cancelRename: () => void;
  onFolderNavigate?: (folderId?: string | null) => void;
  currentFolderId?: string | null;
  folderEditState: EditingNameState;
  handleRenameFolder?: (id: string, name: string) => void;
  cancelRenameFolder?: (id: string) => void;
  countFilesInFolder?: (folderId: string) => number;
  folderCountKind?: 'file' | 'workflow';
  showFolderCounts?: boolean;
  isSearching?: boolean;
  onMoveItem?: (itemId: string, itemType: DashboardItem, targetFolderId: string) => void;
  isWorkspaceFiles?: boolean;
  /** Tools ignore folders, so every row passed in is shown. */
  flattenFolders?: boolean;
}

function DashboardListView({
  recipes,
  allRecipes,
  folders = [],
  allFolders = [],
  recipeEditState,
  handleRenameRecipe,
  cancelRename,
  handleContextMenu,
  onFolderNavigate,
  currentFolderId,
  folderEditState,
  handleRenameFolder,
  cancelRenameFolder,
  countFilesInFolder,
  folderCountKind = 'file',
  showFolderCounts = true,
  isSearching = false,
  onMoveItem,
  isWorkspaceFiles = false,
  flattenFolders = false,
}: DashboardListViewProps) {
  const foldersForRecursion = allFolders || folders;
  const recipesForLatestUpdate = allRecipes || recipes;
  const goToWorkflow = useGoToWorkflow();
  const isFplMigrationEnabled = useFplMigrationFlag();

  const getSubtreeUpdatedMs = useMemo(
    () => createGetLatestUpdateInFolder(foldersForRecursion, recipesForLatestUpdate),
    [foldersForRecursion, recipesForLatestUpdate],
  );

  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<AllowedRecipeKeys>('updatedAt');
  const currentUser = useUserStore((state) => state.user);
  const { t } = useTranslation();
  const { dragState, handleDragStart, handleDragEnd, handleDragOver, handleDragLeave, handleDrop } = useDragAndDrop();

  const folderWorkflowData = useMemo(
    () => calculateFolderRecipeData(folders, recipes, allRecipes, allFolders, isFplMigrationEnabled),
    [folders, recipes, allRecipes, allFolders, isFplMigrationEnabled],
  );

  const handleRequestSort = (_e: React.MouseEvent<unknown>, property: AllowedRecipeKeys) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Takes the row itself: looking it up by `id` found the wrong one of a both-ways share's two rows,
  // opening the workflow editor from the row labelled "Tool".
  const handleClick = (evt: React.MouseEvent<HTMLDivElement>, workflow: DashboardRecipe) => {
    if (evt.detail == 1) {
      goToWorkflow(workflow);
    }
  };

  const visibleFolders = folders;
  const visibleRecipes =
    flattenFolders || isSearching
      ? recipes // When searching, and on the flat Tools page, show every row passed in
      : currentFolderId
        ? recipes.filter((recipe) => recipe.folderId === currentFolderId)
        : recipes.filter((recipe) => !recipe.folderId);

  const combinedItems: ListItem[] = useMemo(() => {
    const folderItems = visibleFolders.map((folder) => ({ ...folder, type: 'folder' as const }));
    const recipeItems = visibleRecipes.map((recipe) => ({ ...recipe, type: 'recipe' as const }));
    return [...folderItems, ...recipeItems];
  }, [visibleFolders, visibleRecipes]);

  const visibleRows = useMemo(() => {
    const sorted = [...combinedItems].sort((a, b) => {
      if (orderBy === 'updatedAt' || orderBy === 'createdAt') {
        const compareResult =
          getListItemDateSortValue(b, orderBy, getSubtreeUpdatedMs) -
          getListItemDateSortValue(a, orderBy, getSubtreeUpdatedMs);
        return order === 'desc' ? compareResult : -compareResult;
      } else if (orderBy === 'files') {
        const folderColumnCount = (item: ListItem) => {
          if (item.type !== 'folder') return 0;
          const summary = folderWorkflowData[item.id];
          // The Workflows column names workflows, so it sorts by that count, not by subfolders.
          if (folderCountKind === 'workflow') return summary?.contentCount ?? summary?.count ?? 0;
          return summary?.count || 0;
        };
        const filesA = folderColumnCount(a);
        const filesB = folderColumnCount(b);

        const compareResult = filesB - filesA;
        return order === 'desc' ? compareResult : -compareResult;
      } else if (orderBy === 'ownerName') {
        let ownerA = '';
        let ownerB = '';

        if (a.type === 'recipe') {
          ownerA = currentUser && a.owner && a.owner === currentUser.uid ? t(I18N_KEYS.GENERAL.YOU) : a.ownerName || '';
        }
        if (b.type === 'recipe') {
          ownerB = currentUser && b.owner && b.owner === currentUser.uid ? t(I18N_KEYS.GENERAL.YOU) : b.ownerName || '';
        }

        const compareResult = ownerB.toLowerCase().localeCompare(ownerA.toLowerCase());
        return order === 'desc' ? compareResult : -compareResult;
      } else {
        if (a.type === 'folder' && b.type === 'recipe') {
          return -1;
        }
        if (a.type === 'recipe' && b.type === 'folder') {
          return 1;
        }

        // By the title on screen, which on a tool row is the tool's name rather than the workflow's.
        const compareResult =
          orderBy === 'name'
            ? descendingComparator({ name: dashboardDisplayName(a) }, { name: dashboardDisplayName(b) }, 'name')
            : descendingComparator(a, b, orderBy as keyof ListItem);
        return order === 'desc' ? compareResult : -compareResult;
      }
    });

    return sorted;
  }, [combinedItems, order, orderBy, folderWorkflowData, folderCountKind, currentUser, t, getSubtreeUpdatedMs]);

  const handleCancelRenameFolder = useCallback(
    (id: string) => {
      if (cancelRenameFolder) {
        cancelRenameFolder(id);
      }
    },
    [cancelRenameFolder],
  );

  const handleInlineEditConfirm = useCallback(
    (item: ListItem, value: string) => {
      if (item.type === 'folder' && folderEditState.kind === 'editing' && handleRenameFolder) {
        handleRenameFolder(folderEditState.id, value);
      } else if (item.type === 'recipe' && recipeEditState.kind === 'editing') {
        handleRenameRecipe(recipeEditState.id, value);
      }
    },
    [folderEditState, handleRenameFolder, handleRenameRecipe, recipeEditState],
  );

  const handleInlineEditCancel = useCallback(
    (item: ListItem) => {
      if (item.type === 'folder' && folderEditState.kind === 'editing') {
        handleCancelRenameFolder(folderEditState.id);
      } else if (item.type === 'recipe' && recipeEditState.kind === 'editing') {
        cancelRename();
      }
    },
    [cancelRename, folderEditState, handleCancelRenameFolder, recipeEditState],
  );

  if (!recipes.length && !folders.length) {
    return null;
  }

  return (
    <Box data-testid="dashboard-table-view-container" sx={{ pb: 4 }}>
      <AppTable.TableContainer>
        <AppTable
          aria-labelledby="tableTitle"
          sx={{
            '& .MuiTableCell-root': {
              borderBottom: 'none',
            },
          }}
        >
          <EnhancedTableHead
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            isWorkspaceFiles={isWorkspaceFiles}
            showFilesColumn={showFolderCounts}
            filesColumnLabel={
              folderCountKind === 'workflow' ? t(I18N_KEYS.DASHBOARD.PAGES.WORKFLOWS.TITLE) : t(I18N_KEYS.GENERAL.FILES)
            }
          />
          <AppTable.Tbody>
            {visibleRows.map((item) => {
              const isFolder = item.type === 'folder';
              const isEditingFolder = folderEditState.kind === 'editing';
              const isEditingRecipe = recipeEditState.kind === 'editing';
              const isEditingThis = isFolder
                ? isEditingFolder && folderEditState.id === item.id
                : isEditingRecipe && recipeEditState.id === item.id;
              const canInteract = isFolder ? !isEditingFolder : !isEditingRecipe;

              const rowId = isFolder ? item.id : dashboardRowId(item);
              const updatedDisplayMs = getListItemDateSortValue(item, 'updatedAt', getSubtreeUpdatedMs);
              const createdDisplayMs = getListItemDateSortValue(item, 'createdAt', getSubtreeUpdatedMs);
              const folderSummary = isFolder ? folderWorkflowData[item.id] : undefined;
              const folderCount = !isFolder
                ? 0
                : isSearching && countFilesInFolder
                  ? countFilesInFolder(item.id)
                  : folderCountKind === 'workflow'
                    ? (folderSummary?.contentCount ?? folderSummary?.count ?? 0)
                    : folderSummary?.count || 0;
              const folderCountLabel =
                folderCountKind === 'workflow'
                  ? t(
                      folderCount === 1
                        ? I18N_KEYS.MAIN_DASHBOARD.FOLDER_CARD.WORKFLOW_COUNT_SINGULAR
                        : I18N_KEYS.MAIN_DASHBOARD.FOLDER_CARD.WORKFLOW_COUNT_PLURAL,
                      { count: folderCount },
                    )
                  : `${folderCount} ${t(folderCount === 1 ? I18N_KEYS.GENERAL.FILE : I18N_KEYS.GENERAL.FILES)}`;

              return (
                <AppTable.Tr
                  hover
                  onClick={(event) => {
                    if (!canInteract) {
                      return;
                    }
                    if (isFolder && onFolderNavigate) {
                      onFolderNavigate(item.id);
                    } else if (item.type === 'recipe') {
                      handleClick(event, item);
                    }
                  }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleContextMenu(e, item.id, isFolder ? 'folder' : 'workflow', rowId);
                  }}
                  role="checkbox"
                  tabIndex={-1}
                  key={rowId}
                  sx={{
                    cursor: (isFolder ? isEditingThis : !canInteract) ? 'default' : 'grab',
                    border: 'none',
                    opacity: dragState.draggedItemId === item.id ? 0.5 : 1,
                    backgroundColor:
                      isFolder && dragState.dragOverItemId === item.id && dragState.draggedItemId !== item.id
                        ? isFplMigrationEnabled
                          ? cssVars.color.bgSecondary
                          : token.bgSecondary
                        : 'transparent',
                    transition: 'opacity 0.2s, background-color 0.2s',
                  }}
                  draggable={canInteract}
                  onDragStart={(e) => {
                    if (canInteract) {
                      handleDragStart(e, item.id, isFolder ? 'folder' : 'workflow');
                    }
                  }}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => {
                    if (isFolder) {
                      e.preventDefault();
                      handleDragOver(e, item.id);
                    }
                  }}
                  onDragLeave={() => isFolder && handleDragLeave()}
                  onDrop={(e) => {
                    if (isFolder) {
                      e.preventDefault();
                      handleDrop(e, item.id, (itemId, itemType, targetFolderId) => {
                        if (itemId !== targetFolderId) {
                          onMoveItem?.(itemId, itemType, targetFolderId);
                        }
                      });
                    }
                  }}
                >
                  <AppTable.Td component="th" scope="row" padding="none" sx={{ p: 0, borderRadius: `8px 0 0 8px` }}>
                    <FlexCenVer sx={{ p: 1 }}>
                      <Box
                        sx={{
                          width: '144px',
                          height: '72px',
                          flexShrink: 0,
                          borderRadius: 2,
                          position: 'relative',
                          border: `1px solid ${color.White08_T}`,
                          overflow: 'hidden',
                        }}
                      >
                        {isFolder ? (
                          <FolderThumbnail
                            posters={folderWorkflowData[item.id]?.posters ?? []}
                            count={folderWorkflowData[item.id]?.count ?? 0}
                          />
                        ) : (
                          <img
                            alt=""
                            loading="lazy"
                            style={{
                              display: 'block',
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              borderRadius: 1,
                            }}
                            src={item.poster || defaultWorkflowCover(isFplMigrationEnabled, 'list')}
                          />
                        )}
                      </Box>
                      <Box data-testid="dashboard-list-view-recipe-name-container" sx={{ ml: 3 }}>
                        <ListRowName
                          isEditing={isEditingThis}
                          name={item.name}
                          displayValue={dashboardDisplayName(item)}
                          onConfirm={(value) => handleInlineEditConfirm(item, value)}
                          onCancel={() => handleInlineEditCancel(item)}
                        />
                      </Box>
                    </FlexCenVer>
                  </AppTable.Td>
                  {isWorkspaceFiles && (
                    <AppTable.Td align="center">
                      <TextSmRg>
                        {!isFolder && item.type === 'recipe'
                          ? currentUser && item.owner && item.owner === currentUser.uid
                            ? t(I18N_KEYS.GENERAL.YOU)
                            : item.ownerName || '-'
                          : '-'}
                      </TextSmRg>
                    </AppTable.Td>
                  )}
                  {showFolderCounts ? (
                    <AppTable.Td align="center">
                      <TextSmRg>{isFolder ? folderCountLabel : '-'}</TextSmRg>
                    </AppTable.Td>
                  ) : null}
                  <AppTable.Td align="center">
                    <TextSmRg>{updatedDisplayMs ? getTimeDiffInWords(updatedDisplayMs) : '-'}</TextSmRg>
                  </AppTable.Td>
                  <AppTable.Td align="center" sx={{ borderRadius: '0 8px 8px 0' }}>
                    <TextSmRg>{createdDisplayMs ? getTimeDiffInWords(createdDisplayMs) : '-'}</TextSmRg>
                  </AppTable.Td>
                </AppTable.Tr>
              );
            })}
          </AppTable.Tbody>
        </AppTable>
      </AppTable.TableContainer>
    </Box>
  );
}

export default DashboardListView;
